import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import i18n from '../i18n-config';
import { DEFAULT_LANGUAGE } from '../types/i18n-types';
import { applyDocumentDirection } from '../utils/rtl-utils';
import { useI18nLanguagesStore } from './i18n-languages-store';
import { ApplicationType } from '@/features/translations/types/translation-types';

/**
 * i18n Store State Interface
 */
interface I18nState {
  /** Current language code */
  currentLanguage: string;
  /** Whether current language is RTL */
  isRtl: boolean;
  /** Whether translations are loading */
  isLoading: boolean;
  /** Error message if any */
  error: string | null;
  /** Whether the store has been initialized */
  isInitialized: boolean;
  /** Application type for fetching translations from API */
  applicationType: ApplicationType;
  /** Cached translations from API: { "en": { "key": "value" }, "he": { ... } } */
  translationCache: Record<string, Record<string, string>> | null;
  /** Whether translations are being fetched from API */
  isFetchingTranslations: boolean;
  /** Error from translation fetch */
  translationFetchError: string | null;
  /** Whether currently using fallback local translations (API failed) */
  isUsingFallback: boolean;
}

/**
 * i18n Store Actions Interface
 */
interface I18nActions {
  /** Change the current language */
  changeLanguage: (langCode: string) => Promise<void>;
  /** Clear error state */
  clearError: () => void;
  /** Initialize i18n (called on app start) */
  initialize: () => Promise<void>;
  /** Set application type for translations (clears cache) */
  setApplicationType: (appType: ApplicationType) => void;
  /** Set translation cache (called by repository after API fetch) */
  setTranslationCache: (cache: Record<string, Record<string, string>>) => void;
  /** Clear translation cache (forces refetch) */
  clearTranslationCache: () => void;
  /** Get translations for a specific language from cache */
  getTranslationsForLanguage: (lang: string) => Record<string, string> | null;
  /** Set fallback mode status */
  setIsUsingFallback: (isUsingFallback: boolean) => void;
}

/**
 * Combined i18n Store Type
 */
type I18nStore = I18nState & I18nActions;

/**
 * Check if a language is RTL using the languages store
 * Falls back to hardcoded list if API data not available
 */
function isRtlLanguage(langCode: string): boolean {
  const languagesStore = useI18nLanguagesStore.getState();
  
  // Try to get from API data first
  if (languagesStore.hasFetched && languagesStore.languages.length > 0) {
    return languagesStore.isRtlLanguage(langCode);
  }
  
  // Fallback to hardcoded list if API not loaded yet
  const rtlCodes = ['he', 'ar', 'fa', 'ur'];
  return rtlCodes.includes(langCode.toLowerCase().split('-')[0]);
}

/**
 * Zustand store for i18n state management
 * Persists language preference to localStorage
 */
export const useI18nStore = create<I18nStore>()(
  persist(
    (set, get) => ({
      // Initial state
      currentLanguage: DEFAULT_LANGUAGE,
      isRtl: false,
      isLoading: false,
      error: null,
      isInitialized: false,
      applicationType: ApplicationType.ReactWeb,
      translationCache: null,
      isFetchingTranslations: false,
      translationFetchError: null,
      isUsingFallback: false,

      // Actions
      changeLanguage: async (langCode: string) => {
        const { currentLanguage } = get();

        // Skip if same language
        if (langCode === currentLanguage) {
          return;
        }

        set({ isLoading: true, error: null });

        try {
          // Change i18next language
          await i18n.changeLanguage(langCode);

          // Determine if RTL
          const isRtl = isRtlLanguage(langCode);

          // Update document direction for RTL support
          applyDocumentDirection(langCode, isRtl);

          set({
            currentLanguage: langCode,
            isRtl,
            isLoading: false,
          });

          if (import.meta.env.DEV) {
            console.log(`[i18n] Language changed to: ${langCode} (RTL: ${isRtl})`);
          }
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : 'Failed to change language';
          console.error('[i18n] Error changing language:', error);
          set({
            error: errorMessage,
            isLoading: false,
          });
        }
      },

      clearError: () => set({ error: null }),

      initialize: async () => {
        const { currentLanguage, isInitialized } = get();

        // Only initialize once
        if (isInitialized) {
          return;
        }

        // Determine if RTL
        const isRtl = isRtlLanguage(currentLanguage);

        // Apply initial direction
        applyDocumentDirection(currentLanguage, isRtl);

        // Set RTL state
        set({
          isRtl,
          isInitialized: true,
        });

        // Ensure i18next is in sync with stored preference
        if (i18n.language !== currentLanguage) {
          try {
            await i18n.changeLanguage(currentLanguage);
          } catch (error) {
            console.error('[i18n] Error syncing language on init:', error);
          }
        }

        if (import.meta.env.DEV) {
          console.log(`[i18n] Initialized with language: ${currentLanguage} (RTL: ${isRtl})`);
        }
      },

      setApplicationType: (appType: ApplicationType) => {
        const { applicationType } = get();
        if (appType !== applicationType) {
          set({
            applicationType: appType,
            translationCache: null, // Clear cache when app type changes
            translationFetchError: null,
          });
          if (import.meta.env.DEV) {
            console.log(`[i18n] Application type changed to: ${appType}`);
          }
        }
      },

      setTranslationCache: (cache: Record<string, Record<string, string>>) => {
        set({ translationCache: cache, translationFetchError: null, isUsingFallback: false });
      },

      clearTranslationCache: () => {
        set({ translationCache: null, isUsingFallback: false });
      },

      getTranslationsForLanguage: (lang: string) => {
        const { translationCache } = get();
        if (!translationCache) return null;
        return translationCache[lang.toLowerCase()] || null;
      },

      setIsUsingFallback: (isUsingFallback: boolean) => {
        set({ isUsingFallback });
      },
    }),
    {
      name: 'i18n-storage',
      // Only persist the language preference
      partialize: (state) => ({
        currentLanguage: state.currentLanguage,
      }),
      // Rehydrate callback - sync direction on load
      onRehydrateStorage: () => (state) => {
        if (state) {
          // Apply direction immediately on rehydrate
          const isRtl = isRtlLanguage(state.currentLanguage);
          applyDocumentDirection(state.currentLanguage, isRtl);
          state.isRtl = isRtl;
        }
      },
    }
  )
);
